import { useState } from 'react'
import { KeepPosted } from '@/components/KeepPosted'
import { ActionBar, type ActionItem } from '@/components/ActionBar'
import { useNudgeMembersItem } from '@/components/NudgeMembersButton'
import { useShareWithItem } from '@/components/ShareWith'
import { PinProject } from '@/components/PinProject'
import { deleteWithUndo } from '@/lib/actions'
import { Link, useNavigate } from '@tanstack/react-router'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { mutate } from '@/runtime'
import { useAllRuns, useEdgesTo, useMirrors, useProjectOf, useProjects, useTasks } from '@/db/hooks'
import { NewAgentSheet, isLead } from '@/kinds/agentrun/components'
import { RunTag } from '@/components/RunStatus'
import { useAgentsHere, useCodeWorkHere } from '@/hooks/useCodeWork'
import { PROJECT_STATE_WORD } from '@/lib/words'
import { TryButton } from '@/components/PreviewButton'
import { PublishSection } from '@/components/PublishSection'
import { ProjectBoard } from '@/components/ProjectBoard'
import type { Edge } from '@/db/types'
import { IssueListRow, MirrorSheet, PullListRow } from '@/kinds/git/components'
import type { GitIssueResource, PullRequestResource } from '@/kinds/git/apply'
import { PagesSection } from '@/kinds/page/components'
import { ListsSection } from '@/kinds/list/components'
import { BecameBanner, ConvertSheet } from '@/components/ConvertSheet'
import { useAllPages } from '@/db/hooks'
import { RemindPicker } from '@/components/RemindPicker'
import { Icon } from '@/lib/icons'
import type { Resource } from '@/db/types'
import { useListNav } from '@/hooks/useKeyboard'
import { KindError } from '@/kinds/kind'
import { isOpen, isTemplate } from '@/kinds/task/schema'
import { compareToday } from '@/lib/today'
import { todayLocal, fmtDateInPhrase } from '@/lib/time'
import { useUi } from '@/state/ui'
import { QuickAdd } from '@/components/QuickAdd'
import { TaskRow } from '@/components/TaskRow'
import { Button, Field, IconButton, Segmented, Sheet, Tag } from '@/components/ui'
import { IconButtonField } from '@/components/EmojiPicker'
import { ColourPicker } from '@/components/ColourPicker'
import { tintText, type Hue } from '@/lib/colour'
import { projectSpec, type ProjectSpec, type ProjectStatus } from './schema'
import type { ProjectResource } from './apply'

type Props = { resource: Resource<ProjectSpec, ProjectStatus> }

export const ProjectListRow = ({ resource }: Props) => (
  <Link
    to="/projects/$id"
    params={{ id: resource.id }}
    className="block rounded-lg bg-panel/60 px-3 py-2 min-h-12"
  >
    <Icon value={resource.spec.icon} className="mr-1" />
    {resource.spec.title}
  </Link>
)

/** highest priority, then earliest scheduled, then smallest estimate — same as the server's next_task_id. */
const compareProjectTasks = (
  a: Parameters<typeof compareToday>[0],
  b: Parameters<typeof compareToday>[1],
) => {
  if (a.spec.priority !== b.spec.priority) return b.spec.priority - a.spec.priority
  const sa = a.spec.scheduled ?? ''
  const sb = b.spec.scheduled ?? ''
  if (sa !== sb) {
    if (!sa) return 1
    if (!sb) return -1
    return sa < sb ? -1 : 1
  }
  return compareToday(a, b)
}

/** The projects that sit under this one, and a way to add one. */
const SubProjectsSection = ({ project, edges }: { project: ProjectResource; edges: Edge[] }) => {
  const projects = useProjects() ?? []
  const [adding, setAdding] = useState(false)
  const [title, setTitle] = useState('')
  const parentOf = useProjectOf()
  const subs = edges
    .filter((e) => e.relation === 'belongs_to')
    .map((e) => projects.find((p) => p.id === e.from_id))
    .filter((p): p is ProjectResource => !!p && !p.deleted_at)
    .sort((a, b) => a.spec.title.localeCompare(b.spec.title))
  const parent = projects.find((p) => p.id === parentOf.get(project.id))
  const add = async () => {
    const t = title.trim()
    if (!t) return
    const r = await mutate.create('Project', { title: t }, { ...project.labels })
    await mutate.setProject(r.id, project.id)
    setTitle('')
    setAdding(false)
  }
  if (!subs.length && !adding && !parent) {
    return (
      <div className="mt-6 flex items-center justify-between">
        <span className="text-[13px] font-semibold text-muted">Sub-projects</span>
        <Button variant="chip" onClick={() => setAdding(true)} data-testid="subproject-add">
          New sub-project
        </Button>
      </div>
    )
  }
  return (
    <section className="mt-6" data-testid="project-subprojects">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[13px] font-semibold text-muted">Sub-projects</span>
        <Button variant="chip" onClick={() => setAdding(true)} data-testid="subproject-add">
          New sub-project
        </Button>
      </div>
      {parent && (
        <p className="text-xs text-faint mb-2">
          Sits under{' '}
          <Link to="/projects/$id" params={{ id: parent.id }} className="underline">
            {parent.spec.title}
          </Link>
        </p>
      )}
      <ul className="group-list">
        {subs.map((p) => (
          <li key={p.id}>
            <Link to="/projects/$id" params={{ id: p.id }} className="block px-3 py-2.5 min-h-12">
              <span className="font-medium flex items-center gap-2">
                <Icon value={p.spec.icon} size={16} />
                {p.spec.title}
              </span>
              {p.status.open_tasks > 0 && (
                <span className="block text-xs text-muted">{p.status.done_tasks} done</span>
              )}
            </Link>
          </li>
        ))}
      </ul>
      {adding && (
        <form
          className="flex gap-2 mt-2"
          onSubmit={(e) => {
            e.preventDefault()
            void add()
          }}
        >
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Sub-project"
            className="flex-1"
            autoFocus
            data-testid="subproject-title"
          />
          <Button type="submit">Add</Button>
        </form>
      )}
    </section>
  )
}

export const ProjectDetail = ({ resource: project }: Props) => {
  const nudgeItem = useNudgeMembersItem(project.id, project.labels.ws)
  const shareWithItem = useShareWithItem({
    kind: 'Project',
    id: project.id,
    title: project.spec.title,
    ws: project.labels.ws,
  })
  const navigate = useNavigate()
  const [editing, setEditing] = useState(false)
  const [showDone, setShowDone] = useState(false)
  const edges = useEdgesTo(project.id)
  const tasks = useTasks() ?? []
  const [converting, setConverting] = useState(false)
  const allPages = useAllPages() ?? []
  const projectTasks = tasks.filter((t) =>
    edges.some((e) => e.relation === 'belongs_to' && !e.deleted_at && e.from_id === t.id),
  )
  const repos = new Set(project.spec.repos ?? [])
  const issues = ((useMirrors('GitIssue') ?? []) as GitIssueResource[]).filter(
    (i) => repos.has(i.spec.repo) && i.status.state === 'open',
  )
  const pulls = ((useMirrors('PullRequest') ?? []) as PullRequestResource[]).filter(
    (p) => repos.has(p.spec.repo) && p.status.state === 'open',
  )
  const [openMirror, setOpenMirror] = useState<GitIssueResource | PullRequestResource | null>(null)
  // coding agents work on the project's repos with its notes, pages and open tasks as their brief
  const factory = useAgentsHere(project.labels.ws, project.spec.repos)
  const [startingAgent, setStartingAgent] = useState(false)
  const allRuns = useAllRuns() ?? []
  const agents = allRuns
    .filter((r) => r.spec.project_id === project.id && !r.deleted_at && !isLead(r))
    .slice(0, 6)
  // list or board, remembered on this device
  const [view, setView] = useState<'list' | 'board'>(() => {
    try {
      return localStorage.getItem('project-view') === 'board' ? 'board' : 'list'
    } catch {
      return 'list'
    }
  })
  const pickView = (v: 'list' | 'board') => {
    setView(v)
    try {
      localStorage.setItem('project-view', v)
    } catch {
      // private window: the choice lasts this visit
    }
  }
  const setQuickAdd = useUi((s) => s.setQuickAdd)
  const mine = edges
    .filter((e) => e.relation === 'belongs_to')
    .map((e) => tasks.find((t) => t.id === e.from_id))
    .filter((t): t is NonNullable<typeof t> => !!t && !t.deleted_at && !isTemplate(t.spec))
  const open = mine.filter((t) => isOpen(t.status.state)).sort(compareProjectTasks)
  const done = mine
    .filter((t) => !isOpen(t.status.state))
    .sort((a, b) => ((a.status.completed_at ?? '') < (b.status.completed_at ?? '') ? 1 : -1))
  const cursor = useListNav(open.map((t) => t.id))
  const today = todayLocal()
  const act = (a: string) => () => void mutate.action(project.id, a).catch(() => {})
  const live = project.status.state === 'active' || project.status.state === 'paused'
  const actions: ActionItem[] = [
    {
      id: 'remind',
      label: 'Remind me',
      pin: true,
      render: ({ as }) => (
        <RemindPicker
          as={as}
          value={project.spec.remind_at}
          fired={!!project.spec.remind_at && project.status.reminder_fired_for === project.spec.remind_at}
          onChange={(v) => void mutate.patch(project.id, { spec: { remind_at: v } })}
        />
      ),
    },
    { id: 'pause', label: 'Pause', hidden: project.status.state !== 'active', onSelect: act('pause') },
    { id: 'resume', label: 'Resume', hidden: project.status.state !== 'paused', onSelect: act('resume') },
    { id: 'complete', label: 'Complete', pin: true, hidden: !live, onSelect: act('complete') },
    { ...nudgeItem, hidden: nudgeItem.hidden || !live },
    { ...shareWithItem, hidden: shareWithItem.hidden || !live },
    {
      id: 'convert',
      label: 'Convert to…',
      hidden: !live,
      onSelect: () => setConverting(true),
      testid: 'project-convert',
    },
    { id: 'drop', label: 'Drop', hidden: !live, onSelect: act('drop') },
    {
      id: 'delete',
      label: 'Delete',
      tone: 'danger',
      onSelect: () => void remove(),
      testid: 'project-delete',
    },
  ]
  const remove = async () => {
    if (!confirm('Delete this project? Its tasks stay.')) return
    await deleteWithUndo(project.id, project.spec.title)
    void navigate({ to: '/projects' })
  }
  return (
    <article className="fade-in">
      <div
        className="flex items-start gap-2 mb-1"
        style={project.spec.colour ? { color: tintText(project.spec.colour as Hue) } : undefined}
      >
        <h1 className="text-xl font-semibold flex-1 break-words">
          <Icon value={project.spec.icon} size={20} className="mr-2" />
          {project.spec.title}
        </h1>
        <PinProject id={project.id} pinned={!!project.spec.pinned} />
        <IconButton label="Edit" onClick={() => setEditing(true)}>
          ✎
        </IconButton>
      </div>
      {project.status.converted_to && (
        <BecameBanner
          to={project.status.converted_to}
          titleOf={(id) =>
            tasks.find((t) => t.id === id)?.spec.title ?? allPages.find((p) => p.id === id)?.name
          }
        />
      )}
      <div className="flex flex-wrap gap-1 mb-2">
        {/* an active project says nothing about it; the others say so in a word */}
        {project.status.state !== 'active' && <Tag>{PROJECT_STATE_WORD[project.status.state]}</Tag>}
        {project.spec.due && (
          <Tag tone={project.spec.due < today ? 'warn' : 'muted'}>
            due {fmtDateInPhrase(project.spec.due, today)}
          </Tag>
        )}
      </div>
      {project.spec.outcome && <p className="text-sm text-fg-2 mb-2">Outcome: {project.spec.outcome}</p>}
      {project.spec.notes && (
        <p className="text-sm text-muted whitespace-pre-wrap mb-3">{project.spec.notes}</p>
      )}

      {factory && (
        <section className="mb-4" data-testid="project-agents">
          <div className="flex items-center gap-2 mb-1.5">
            <h2 className="text-[13px] font-semibold text-muted flex-1">Coding agents</h2>
            {/* what is on main, to try: the way in once a prototype's agents have all ended */}
            {repos.size > 0 && <TryButton projectId={project.id} />}
            <Button variant="chip" onClick={() => setStartingAgent(true)} data-testid="project-new-agent">
              New coding agent
            </Button>
          </div>
          {agents.length > 0 && (
            <ul className="group-list">
              {agents.map((r) => (
                <li key={r.id}>
                  <Link
                    to="/agents/run/$id"
                    params={{ id: r.id }}
                    className="flex items-center gap-2 px-3 py-2 min-h-11 text-sm"
                  >
                    <span className="flex-1 min-w-0 truncate">{r.spec.title || r.spec.name}</span>
                    {r.spec.pr_number ? <Tag>PR #{r.spec.pr_number}</Tag> : null}
                    <RunTag run={r} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {startingAgent && <NewAgentSheet project={project} onClose={() => setStartingAgent(false)} />}
        </section>
      )}
      {factory && repos.size > 0 && <PublishSection projectId={project.id} repos={[...repos]} />}

      <ActionBar className="mb-4" items={actions} label="Project actions" testid="project-actions" />

      <KeepPosted id={project.id} labels={project.labels} />
      <div className="flex items-center justify-between gap-2 mb-2">
        <Segmented
          label="View"
          value={view}
          options={[
            { value: 'list', label: 'List' },
            { value: 'board', label: 'Board' },
          ]}
          onChange={pickView}
        />
        <Button variant="chip" onClick={() => setQuickAdd(true)} data-testid="project-add">
          New task
        </Button>
      </div>
      {view === 'board' ? (
        // on a desk the board takes more than the page's column: four columns of titles need it
        <div className="mb-6 lg:relative lg:left-1/2 lg:-translate-x-1/2 lg:w-[max(100%,min(64rem,calc(100vw-20rem)))]">
          <ProjectBoard project={project} tasks={mine} runs={allRuns} />
        </div>
      ) : open.length === 0 ? (
        <p className="text-sm text-faint py-4">No open task. Add the next small step.</p>
      ) : (
        <ul className="group-list" data-testid="project-tasks">
          {open.map((t, i) => (
            <TaskRow key={t.id} task={t} index={i} focused={cursor === i} />
          ))}
        </ul>
      )}
      {view === 'list' && done.length > 0 && (
        <div className="mt-4">
          <button type="button" className="text-xs text-faint min-h-8" onClick={() => setShowDone((s) => !s)}>
            {showDone ? 'Hide finished' : 'Show finished'}
          </button>
          {showDone && (
            <ul className="group-list mt-1">
              {done.map((t) => (
                <TaskRow key={t.id} task={t} mode="compact" />
              ))}
            </ul>
          )}
        </div>
      )}
      {repos.size > 0 && (pulls.length > 0 || issues.length > 0) && (
        <section className="mt-6">
          <h2 className="text-[13px] font-semibold text-muted mb-2">From {[...repos].join(', ')}</h2>
          <ul className="flex flex-col gap-1.5">
            {pulls.map((p) => (
              <PullListRow key={p.id} resource={p} onOpen={setOpenMirror} />
            ))}
            {issues.map((i) => (
              <IssueListRow key={i.id} resource={i} onOpen={setOpenMirror} />
            ))}
          </ul>
          <MirrorSheet resource={openMirror} onClose={() => setOpenMirror(null)} />
        </section>
      )}
      <SubProjectsSection project={project} edges={edges} />
      <PagesSection parentId={project.id} ws={project.labels.ws} />
      <ListsSection parentId={project.id} ws={project.labels.ws} />
      {converting && (
        <ConvertSheet source={project} tasks={projectTasks} onClose={() => setConverting(false)} />
      )}
      <QuickAdd projectId={project.id} />
      {editing && <ProjectForm resource={project} onDone={() => setEditing(false)} />}
    </article>
  )
}

export const ProjectForm = ({ resource, onDone }: { resource?: ProjectResource; onDone: () => void }) => {
  const [error, setError] = useState<string | null>(null)
  const [reposText, setReposText] = useState((resource?.spec.repos ?? []).join('\n'))
  // Repositories and the agents' brief are code work's: a Life project never asks for them
  const code = useCodeWorkHere(resource?.labels.ws, resource?.spec.repos)
  const { register, handleSubmit, formState, control } = useForm<ProjectSpec>({
    // an empty date input is '' — allow it here and drop it on save
    resolver: zodResolver(
      projectSpec.extend({
        due: z.union([z.literal(''), z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'use YYYY-MM-DD')]).optional(),
      }),
    ),
    defaultValues: resource ? { ...resource.spec } : { title: '' },
  })
  const submit = async (v: ProjectSpec) => {
    const repos = (reposText ?? '')
      .split(/[\n,\s]+/)
      .map((r) => r.trim())
      .filter(Boolean)
    const spec: Record<string, unknown> = {
      title: v.title.trim(),
      icon: v.icon || undefined,
      colour: v.colour || undefined,
      notes: v.notes?.trim() || undefined,
      outcome: v.outcome?.trim() || undefined,
      due: v.due || undefined,
      repos: repos.length ? repos : undefined,
    }
    try {
      if (resource) {
        const patch: Record<string, unknown | null> = {}
        for (const key of Object.keys(spec)) {
          if (JSON.stringify((resource.spec as Record<string, unknown>)[key]) !== JSON.stringify(spec[key]))
            patch[key] = spec[key] === undefined ? null : spec[key]
        }
        if (Object.keys(patch).length) await mutate.patch(resource.id, { spec: patch })
      } else {
        await mutate.create(
          'Project',
          Object.fromEntries(Object.entries(spec).filter(([, val]) => val !== undefined)),
        )
      }
      onDone()
    } catch (e) {
      setError(e instanceof KindError ? e.message : (e as Error).message)
    }
  }
  return (
    <Sheet open onClose={onDone} title={resource ? 'Edit project' : 'New project'}>
      <form onSubmit={handleSubmit(submit)} noValidate>
        <div className="flex gap-3 items-start">
          <Controller
            control={control}
            name="icon"
            render={({ field }) => (
              <IconButtonField value={field.value ?? undefined} onChange={field.onChange} />
            )}
          />
          <div className="flex-1">
            <Field label="Title" error={formState.errors.title?.message}>
              <input {...register('title')} className="w-full" autoFocus data-testid="project-title" />
            </Field>
          </div>
        </div>
        <Field label="Colour" hint="Its tasks carry a stripe in this colour">
          <Controller
            control={control}
            name="colour"
            render={({ field }) => (
              <ColourPicker value={field.value as Hue | undefined} onChange={field.onChange} />
            )}
          />
        </Field>
        <Field label="Outcome" hint="What does done look like?">
          <input {...register('outcome')} className="w-full" />
        </Field>
        <Field
          label="Notes"
          hint={
            code
              ? 'What anyone joining needs to know; coding agents get it as their brief'
              : 'What anyone joining needs to know'
          }
        >
          <textarea {...register('notes')} rows={3} className="w-full" />
        </Field>
        <Field label="Due" error={formState.errors.due?.message}>
          <input type="date" {...register('due')} className="w-full" />
        </Field>
        {code && (
          <Field
            label="Repositories"
            hint="owner/repo, one per line; coding agents work here, with this project's notes, pages and open tasks"
          >
            <textarea
              value={reposText}
              onChange={(e) => setReposText(e.target.value)}
              rows={2}
              className="w-full font-mono text-sm"
            />
          </Field>
        )}
        {error && <p className="text-sm text-rose-300 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button onClick={onDone}>Cancel</Button>
          <Button
            variant="primary"
            type="submit"
            disabled={formState.isSubmitting}
            data-testid="project-save"
          >
            Save
          </Button>
        </div>
      </form>
    </Sheet>
  )
}
